import React, { useState, useMemo, useCallback } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import { Layout, Breadcrumb, Avatar, Tag } from 'antd';
import { MenuItem, makeStyles } from '@material-ui/core';
import { EOrderStatus } from 'mdello-client-ts/dist/src/interfaces/providers/ecommerce/order.interface';
import { Address } from 'mdello-client-ts/dist/src/interfaces/providers/ecommerce/common.interface';
import { useUpdateOrder } from 'src/reactQuery/orders/useUpdateOrder';
import { isCurrentProjectBuildy, isCurrentProjectStimull } from 'src/util/projectCheckers';
import { useGetOrderGroupById } from 'src/reactQuery/orders/useGetOrderGroupById';
import NoxTable from 'src/component/Table';
import Copy from 'src/component/Copy';
import { LoanMethod, OrderDetailed } from 'src/reactQuery/orders/interfaces/orderGroup.interface';
import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import { useGetCustomerProfiles } from 'src/reactQuery/customers/queries/useGetCustomerProfiles';
import { useUpdateOrderGroup } from 'src/reactQuery/orders/useUpdateOrderGroup';
import ArrowNextIcon from '../../../asset/arrow-next.svg';
import CButton from '../../../component/Button';
import Loader from '../../../component/Loader/Loader';
import InnerHeader from '../../../component/InnerHeader';
import formatDate from '../../../helpers/formatDate';
import { DefaultSelect } from '../../../component/Select';

const useStyles = makeStyles({
  gridContainer: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '15% 35% 25% 25%',
    gridTemplateRows: 'auto auto',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridContainerBody: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '50% 40% 10%',
    gridTemplateRows: 'auto auto',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridContainerRow2: {
    display: 'grid',
    gridRowGap: '35px',
    gridTemplateColumns: '25% 25% 25% 25%',
    gridTemplateRows: 'auto auto',
    marginLeft: '35px',
    marginTop: '55px'
  },
  gridHeader: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',

    justifyContent: ' center',
    maxWidth: '90%'
  },
  gridItem: {
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemPayment: {
    display: 'flex',
    flexDirection: 'column',
    gridColumn: '3',
    gridRow: '1 / span 2',
    height: '100%',
    justifyContent: 'space-between',
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemSelect: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '90%',
    textAlign: 'start'
  },
  p: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%'
  },
  selectHeader: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    margin: '0px'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 400,
    maxWidth: '100%',
    overflowWrap: 'break-word',
    textAlign: 'start'
  },
  usersInfo: {
    display: 'flex'
  }
});

interface OrdersMapped {
  buyer: string;
  id: string;
  items: number;
  key: number;
  location: string;
  selectedItemId: string;
  selectedItemName: string;
  seller: string;
  status: EOrderStatus;
  total: number;
}

const OrderGroupDetail = () => {
  const history = useHistory();
  const { orderId } = useParams<{ orderId: string }>();
  const classes = useStyles();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { data: orderData, isLoading, refetch: orderRefetch } = useGetOrderGroupById(orderId);
  const { data: profilesData, isLoading: isLoadingProfiles } = useGetCustomerProfiles(
    orderData?.orders.map((order) => order.sellerId)!
  );
  const { mutate } = useUpdateOrder({
    onSuccess: () => {
      orderRefetch();
    }
  });
  const { mutate: mutateOrderGroup } = useUpdateOrderGroup({
    onSuccess: () => {
      orderRefetch();
    }
  });

  const [selectedStatus, setSelectedStatus] = useState<EOrderStatus>();
  const [selectedAddress, setSelectedAddress] = useState<{ address: Address; userId: string }[]>();

  const statuses = [
    'accepted',
    'processing',
    'onHold',
    'cancelled',
    'delivering',
    'delivered',
    'completed',
    'refunded',
    'failed',
    'trash',
    'draft'
  ];

  const selectedOrderGroup = useMemo(() => {
    if (!orderData) return;

    return orderData;
  }, [orderData]);

  const loan = selectedOrderGroup?.loan;

  const sellerAddresses = useMemo(() => {
    if (!profilesData || !selectedOrderGroup) return;

    if (isCurrentProjectStimull) {
      return [];
    }

    const addresses = profilesData
      .map((profile) => {
        return { addresses: profile?.settings.addresses, userId: profile.userId };
      })
      .flat();

    return addresses;
  }, [profilesData, orderId]);

  function itemRender(route: any, params: any, routes: any) {
    const last = routes.indexOf(route) === routes.length - 1;

    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  }

  const routes = [
    {
      breadcrumbName: 'Orders',
      path: '/ecommerce/orders'
    },
    {
      breadcrumbName: selectedOrderGroup?._id,
      path: `/ecommerce/orders/${orderId}`
    }
  ];

  const onUpdate = (status: EOrderStatus) => {
    selectedAddress?.forEach((selectedAddress) => {
      const selectedOrder = selectedOrderGroup?.orders.filter(
        (order) => order.sellerId === selectedAddress.userId
      )[0];

      if (!selectedOrder) {
        return;
      }

      mutate({
        body: {
          address: selectedAddress.address,
          status: selectedOrder.status
        },
        orderId: selectedOrder?._id
      });
    });

    mutateOrderGroup({
      body: status,
      orderId
    });
    history.push('/ecommerce/orders-group');
  };

  const openDetail = (id: string) => {
    history.push({
      pathname: `/ecommerce/orders/${id}`
    });
  };

  const mapToTable = (orders: OrderDetailed[] | undefined) => {
    const data: OrdersMapped[] = [];

    if (!orders || !orders.length) {
      return data;
    }

    orders.forEach(
      (
        { _id, sellerProfile, customerProfile, dropoffAddress, selectedItems, status, totalPrice },
        index
      ) => {
        const total = parseFloat(totalPrice.toString());
        const selectedItemId = selectedItems[0].itemId;
        const selectedItemName = selectedItems[0]?.item?.title;
        data.push({
          buyer: customerProfile?.userName,
          id: _id,
          items: selectedItems.length,
          key: index,
          location: dropoffAddress?.street || '',
          selectedItemId,
          selectedItemName,
          seller: sellerProfile?.userName,
          status,
          total: Number.isNaN(total) ? 0 : parseFloat(total.toFixed(2))
        });
      }
    );

    return data;
  };

  const data = mapToTable(selectedOrderGroup?.orders);

  const columns = [
    {
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => {
        return <Copy text={id} />;
      },
      sorter: (a: OrdersMapped, b: OrdersMapped) => a.id.localeCompare(b.id),
      title: 'Order ID'
    },
    {
      dataIndex: 'seller',
      key: 'seller',
      render: (seller: string) => {
        return <Copy text={seller} />;
      },
      sorter: (a: OrdersMapped, b: OrdersMapped) => a.seller.toString().localeCompare(b.seller),
      title: 'Seller'
    },
    {
      dataIndex: 'buyer',
      key: 'buyer',
      render: (buyer: string) => {
        return <Copy text={buyer} />;
      },
      sorter: (a: OrdersMapped, b: OrdersMapped) => a.buyer.toString().localeCompare(b.buyer),
      title: 'Buyer'
    },
    {
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => {
        return <Copy text={location} />;
      },
      sorter: (a: OrdersMapped, b: OrdersMapped) => a.location.toString().localeCompare(b.location),
      title: 'Delivery Location'
    },
    {
      dataIndex: 'items',
      key: 'items',
      render: (items: number) => {
        return <Copy text={items} />;
      },
      sorter: (a: OrdersMapped, b: OrdersMapped) =>
        a.items.toString().localeCompare(b.items.toString()),
      title: 'Item(s)'
    },
    {
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => {
        return <Copy text={total} />;
      },
      sorter: (a: OrdersMapped, b: OrdersMapped) => a.total - b.total,
      title: 'total'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status: EOrderStatus) => {
        let style = {};
        switch (status) {
          case 'delivered':
            style = {
              backgroundColor: 'rgba(65, 204, 156, 0.15)',
              border: 'none',
              color: '#41CC9C',
              width: '90%'
            };
            break;
          case 'pending':
            style = {
              backgroundColor: 'rgba(155, 160, 173, 0.15)',
              border: 'none',
              color: '#9BA0AD',
              width: '90%'
            };
            break;
          case 'cancelled':
            style = {
              backgroundColor: 'rgba(255, 71, 108, 0.15)',
              border: 'none',
              color: '#FF476C',
              width: '90%'
            };
            break;
          case 'accepted':
            style = {
              backgroundColor: 'rgba(244, 211, 37, 0.15)',
              border: 'none',
              color: '#F4CD00',
              width: '90%'
            };
            break;
          default:
            style = {
              width: '90%'
            };
        }
        return (
          <Tag key={status} style={style}>
            {status}
          </Tag>
        );
      },
      sorter: (a: OrdersMapped, b: OrdersMapped) => a.status.localeCompare(b.status),
      title: 'Status'
    },
    {
      key: 'edit',
      render: (order: OrdersMapped) => {
        return <Tag onClick={() => openDetail(order.id)}>View</Tag>;
      }
    }
  ];

  const OrderHeader = useCallback(() => {
    if (!selectedOrderGroup) return <Loader />;
    const {
      customerProfile,
      createdAt,
      orders,
      totalPayment,
      customerId,
      status
    } = selectedOrderGroup;
    const { profilePictureUrl, userName } = customerProfile;
    const { dropoffAddress } = orders[0];
    const deliveryFee = orders
      .map((order) => {
        return order.deliveryFee;
      })
      .reduce((prev, current) => prev + current);

    const finalDate = formatDate(createdAt);

    return (
      <div
        style={{
          alignItems: 'center'
        }}
      >
        <div
          className={classes.gridContainer}
          style={{
            marginLeft: '35px',
            marginTop: '0px',
            width: '100%'
          }}
        >
          <Avatar
            src={profilePictureUrl ?? '-'}
            style={{
              display: 'flex',
              height: '55px',
              minWidth: '55px',
              width: '55px'
            }}
          />
          <div className={classes.gridHeader}>
            <span className={classes.span}>{userName || '-'}</span>
            <p className={classes.p}>
              ID: <span style={{ color: 'black' }}>{customerId || '-'}</span>
            </p>
          </div>

          <div className={classes.gridHeader}>
            <span className={classes.span}>{finalDate || '-'}</span>
            <p className={classes.p}>Date & Time</p>
          </div>
          <div className={classes.gridItemSelect}>
            <p className={classes.selectHeader}>Status:</p>
            <DefaultSelect
              defaultValue={status}
              onSelect={(value: EOrderStatus) => setSelectedStatus(value)}
              renderValue={(status: EOrderStatus) => status}
              selectStyles={{ marginLeft: '8px' }}
              value={selectedStatus}
            >
              <MenuItem key="asdas" value="status">
                status
              </MenuItem>
              {statuses.map((status) => {
                return (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                );
              })}
            </DefaultSelect>
          </div>
          <div className={classes.gridHeader}>
            <span className={classes.span}>{totalPayment || '-'}</span>
            <p className={classes.p}>
              <span>Total Payment</span>
            </p>
          </div>
          <div className={classes.gridHeader}>
            <span className={classes.span}>{deliveryFee || '-'}</span>
            <p className={classes.p}>
              <span>Delivery Price</span>
            </p>
          </div>
          <div className={classes.gridHeader}>
            <span className={classes.span}>{totalPayment - deliveryFee || '-'}</span>
            <p className={classes.p}>
              <span>Total Price</span>
            </p>
          </div>
          <div className={classes.gridHeader}>
            <span className={classes.span}>{dropoffAddress?.street || '-'}</span>
            <p className={classes.p}>
              <span>Delivery Address</span>
            </p>
          </div>
          {loan && (
            <div className={classes.gridHeader}>
              <span className={classes.span}>{getLabelByLoanMethod(loan.method)}</span>
              <p className={classes.p}>
                <span>Loan Type</span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }, [selectedOrderGroup]);

  const OrderBody = useCallback(() => {
    if (!sellerAddresses) {
      return <Loader />;
    }
    return (
      <div
        className={classes.gridContainerBody}
        style={{
          marginLeft: '35px',
          marginTop: '48px',
          width: '100%'
        }}
      >
        {selectedOrderGroup?.orders.map((order) => {
          const { pickupAddress, sellerProfile } = order;

          const sellerAddress = `${pickupAddress?.name || '-'}, ${pickupAddress?.street || '-'}, ${
            pickupAddress?.apartment || '-'
          }, ${pickupAddress?.phone_number?.country_code || '-'} ${
            pickupAddress?.phone_number?.number || '-'
          }`;

          return (
            <>
              <div className={classes.gridItem}>
                <p className={classes.p}>Seller</p>
                <div className={classes.usersInfo}>
                  <Avatar
                    src={sellerProfile?.profilePictureUrl}
                    style={{
                      display: 'flex',
                      height: '55px',
                      marginRight: '8px',
                      minWidth: '55px',
                      width: '55px'
                    }}
                  />
                  <div className={classes.gridItem}>
                    <span className={classes.span}>{sellerProfile?.userName || '-'}</span>
                    <p className={classes.p}>
                      ID: <span style={{ color: 'black' }}>{sellerProfile?.userId || '-'}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className={classes.gridItem}>
                <p className={classes.p}>Seller Address</p>

                {selectedStatus !== 'delivering' && isCurrentProjectBuildy ? (
                  <DefaultSelect
                    onSelect={(value: Address) => {
                      setSelectedAddress((prevAddresses) => [
                        ...(prevAddresses || []),
                        { address: value, userId: order.sellerId }
                      ]);
                    }}
                    renderValue={(pickupAddress: Address) => {
                      return `${pickupAddress?.name}, ${pickupAddress?.street}, ${pickupAddress?.apartment}`;
                    }}
                    selectStyles={{ marginLeft: '8px' }}
                    value={
                      selectedAddress?.filter((address) => address.userId === order.sellerId)[0]
                        .address ||
                      // @ts-ignore
                      sellerAddresses.filter((address) => address.userId === order.sellerId)[0]
                        .addresses[0]!
                    }
                  >
                    {sellerAddresses
                      ?.filter((sellerAddress) => sellerAddress.userId === order.sellerId)[0]
                      .addresses?.map((address) => {
                        return (
                          // @ts-ignore
                          <MenuItem key={address._id} value={address}>
                            {`${address?.name}, ${address?.street}, ${address?.apartment}`}
                          </MenuItem>
                        );
                      })}
                  </DefaultSelect>
                ) : (
                  <span className={classes.span}>{sellerAddress || '-'}</span>
                )}
              </div>
              <div className={classes.gridItem} />
            </>
          );
        })}
      </div>
    );
  }, [selectedOrderGroup, sellerAddresses]);

  if (isLoading || isLoadingProfiles) {
    return <Loader />;
  }

  return (
    <Layout style={{ background: 'white' }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '35px 40px 0px 35px'
        }}
      >
        <Breadcrumb
          itemRender={itemRender}
          // @ts-ignore
          routes={routes}
          separator={<img alt="separator icon" src={ArrowNextIcon} />}
          style={{
            background: 'white',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        />
        <div>
          <CButton onClick={() => onUpdate(selectedStatus || EOrderStatus.pending)} type="primary">
            Save
          </CButton>

          <CButton onClick={() => onUpdate(EOrderStatus.cancelled)} type="primary">
            Cancel Order
          </CButton>
        </div>
      </div>
      <div style={{ marginTop: '55px', padding: '0px 40px 0px 35px' }}>
        {OrderHeader()}
        {OrderBody()}
      </div>
      <div
        style={{
          height: 'calc(100vh - 165px)',
          marginTop: '80px',
          overflowY: 'auto'
        }}
      >
        <InnerHeader subtitle={orderData?.orders?.length} title="orders" />
        <NoxTable
          checkbox={false}
          columns={columns}
          data={data}
          searchBy={SearchBy.TITLE}
          // @ts-ignore
          selectedRowKeys={selectedRowKeys}
          // @ts-ignore
          setSelectedRowKeys={setSelectedRowKeys}
        />
      </div>
    </Layout>
  );
};

function getLabelByLoanMethod(method?: LoanMethod): string {
  switch (method) {
    case LoanMethod.BNPL:
      return 'განვადება';
    case LoanMethod.BOG_LOAN:
      return 'ნაწილ-ნაწილ';
    default:
      return 'unknown';
  }
}

export default OrderGroupDetail;
